/**
 * Guidance Conversation booking API — pay-first, charge-on-selection flow.
 * Kept separate from servicesService.js/bookingService so the existing
 * real-time BookConsultation flow is never touched.
 */

import { API_ENDPOINTS, BUSINESS_ID } from '../config/api';

export async function getGuidanceService() {
  const res = await fetch(API_ENDPOINTS.services.public({ category: 'guidance_call', businessId: BUSINESS_ID }));
  if (!res.ok) throw new Error('Failed to load guidance conversation service');
  const data = await res.json();
  const service = (data.services || [])[0];
  if (!service) throw new Error('Guidance conversation service is not available right now');
  return service;
}

export async function createGuidancePaymentIntent(serviceId, amount) {
  const res = await fetch(API_ENDPOINTS.guidanceBookings.createPaymentIntent, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ serviceId, amount: amount || undefined }),
  });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || data.message || 'Failed to start payment');
  return data;
}

/** Called immediately once the card is authorized — creates the booking record before any time is picked. */
export async function authorizeGuidanceBooking({ serviceId, stripePaymentIntentId }) {
  const res = await fetch(API_ENDPOINTS.guidanceBookings.authorize, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ serviceId, stripePaymentIntentId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || 'Failed to record authorization');
  return data;
}

/**
 * Called when the customer picks one of the 3 times — charges the card and finalizes the booking.
 * Pass `authHeaders` (from useCustomerAuth().getAuthHeaders()) when the customer is logged in so
 * the booking links to their real account instead of just matching by typed email.
 */
export async function confirmGuidanceBookingSelection({
  bookingId,
  stripePaymentIntentId,
  selectedIso,
  preferredTimeWindows,
  customerName,
  customerEmail,
  customerPhone,
  deliveryMethod,
  notes,
  _hp,
  authHeaders = {},
}) {
  const res = await fetch(API_ENDPOINTS.guidanceBookings.confirmSelection(bookingId), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      stripePaymentIntentId,
      selectedIso,
      preferredTimeWindows,
      customerName,
      customerEmail,
      customerPhone: customerPhone || undefined,
      deliveryMethod: deliveryMethod || 'phone',
      notes: notes || undefined,
      _hp: _hp || undefined,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || 'Booking failed');
  return data;
}

/**
 * Logs demand for a day/window that doesn't currently fit the published availability —
 * no payment, requires a logged-in customer (authHeaders) so we can notify them later.
 */
export async function requestGuidanceTime({ serviceId, timezone, preferredDays, windowStart, windowEnd, notes, authHeaders = {} }) {
  const res = await fetch(API_ENDPOINTS.guidanceBookings.requestTime, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ serviceId, timezone, preferredDays, windowStart, windowEnd, notes: notes || undefined }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || 'Could not submit your request');
  return data;
}
